// react
import { useContext, useEffect, useMemo, useState } from 'react';

// router
import { useNavigate } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextProject } from '../../Project/Project.context';
import { ContextBanner } from '../../../../App.context';
import { ContextSpendPlan } from '../ProjectV2.context';

// services
import { fetchSpendPlans } from '../../../../services/api';

// mui
import {
	alpha,
	Avatar,
	Box,
	Chip,
	Collapse,
	Grid,
	IconButton,
	LinearProgress,
	Paper,
	Stack,
	Tab,
	Tabs,
	Typography,
	useTheme,
} from '@mui/material';

// icons
import {
	AccountBalance as IconAccount,
	AccountTree as IconActivity,
	ArrowBack as IconBack,
	AttachMoney as IconMoney,
	ExpandLess as IconCollapse,
	ExpandMore as IconExpand,
	Inventory2 as IconEnvelope,
	Payments as IconSpendPlan,
	PieChart as IconPieChart,
	Savings as IconEarmark,
	Timeline as IconTimeline,
	UnfoldLess as IconUnfoldLess,
	UnfoldMore as IconUnfoldMore,
} from '@mui/icons-material';

// recharts
import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Legend,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
	Treemap,
	XAxis,
	YAxis,
} from 'recharts';

// components
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';
import { StatCardRow } from '../../../ui/StatCardRow/StatCardRow';

// data
import { COLORS } from '../../../../data';

// design system
import { radius } from '../../../../utils/designSystem';

// utils
import { formatCurrency } from '../../../../utils/currency';
import { getProjectReportingCurrency } from '../../../../utils/fxRate';
import { resolveSpendPlanFxRate } from '../../../../utils/fxRate';

// types
import { SpendPlan } from '../../../../@types';

// Types

interface EarmarkActivity {
	id: number;
	name: string;
	amount: number;
	status: string;
	component?: string;
	category?: string;
	amountLocal?: string | null;
}

interface EarmarkSpendPlan {
	id: number;
	name: string;
	totalPlanned: number;
	status: string;
	currency: string;
	dateStart: string;
	dateEnd: string;
	activities: EarmarkActivity[];
	totalPlannedLocal?: string | null;
}

interface EarmarkAccount {
	id: number;
	name: string;
	institution: string;
	currency: string;
	balance: number;
	spendPlans: EarmarkSpendPlan[];
	totalEarmarked: number;
	unearmarked: number;
}

interface TabPanelProps {
	children?: React.ReactNode;
	index: number;
	value: number;
}

// Color palette for charts

const CHART_COLORS = [
	'#2563eb',
	'#16a34a',
	'#ea580c',
	'#9333ea',
	'#0891b2',
	'#dc2626',
	'#ca8a04',
	'#4f46e5',
	'#059669',
	'#e11d48',
	'#7c3aed',
	'#0284c7',
	'#d97706',
	'#15803d',
	'#be185d',
];

const STATUS_COLORS: Record<string, string> = {
	ACTIVE: '#16a34a',
	COMPLETED: '#2563eb',
	PLANNED: '#ea580c',
	IN_PROGRESS: '#0891b2',
	CANCELLED: '#dc2626',
	DELAYED: '#ca8a04',
};

// Sub-Components

const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => (
	<div role="tabpanel" hidden={value !== index} {...other}>
		{value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
	</div>
);

// Hierarchical Envelope Node for an Activity (leaf)
const ActivityNode = ({ activity, currency }: { activity: EarmarkActivity; currency: string }) => {
	const theme = useTheme();
	return (
		<Stack
			direction="row"
			alignItems="center"
			spacing={2}
			sx={{
				py: 1.5,
				px: 2,
				ml: 8,
				borderLeft: `2px solid ${alpha(theme.palette.primary.main, 0.15)}`,
				'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.03) },
				transition: 'background-color 0.15s',
			}}>
			<Avatar
				sx={{
					width: 32,
					height: 32,
					bgcolor: alpha(STATUS_COLORS[activity.status] || '#6b7280', 0.1),
				}}>
				<IconActivity sx={{ fontSize: 16, color: STATUS_COLORS[activity.status] || '#6b7280' }} />
			</Avatar>
			<Box sx={{ flex: 1, minWidth: 0 }}>
				<Typography
					sx={{
						fontSize: '1.1rem',
						fontWeight: 500,
						whiteSpace: 'nowrap',
						overflow: 'hidden',
						textOverflow: 'ellipsis',
					}}>
					{activity.name}
				</Typography>
				{activity.component && (
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography sx={{ fontSize: '0.9rem', color: '#6b7280' }}>
							{activity.component}
						</Typography>
						{activity.category && (
							<>
								<Typography sx={{ fontSize: '0.75rem', color: '#9ca3af' }}>|</Typography>
								<Typography sx={{ fontSize: '0.9rem', color: '#9ca3af' }}>
									{activity.category}
								</Typography>
							</>
						)}
					</Stack>
				)}
			</Box>
			<Chip
				label={activity.status.replace('_', ' ')}
				size="small"
				sx={{
					bgcolor: alpha(STATUS_COLORS[activity.status] || '#6b7280', 0.1),
					color: STATUS_COLORS[activity.status] || '#6b7280',
					fontWeight: 600,
					fontSize: '0.8rem',
				}}
			/>
			<Box sx={{ minWidth: 120, textAlign: 'right' }}>
				<Typography
					sx={{
						fontSize: '1.1rem',
						fontWeight: 600,
						fontFeatureSettings: '"tnum"',
					}}>
					{formatCurrency({ value: activity.amount, currency })}
				</Typography>
				{activity.amountLocal && (
					<Typography sx={{ fontSize: '0.85rem', color: '#6b7280', fontFeatureSettings: '"tnum"' }}>
						{activity.amountLocal}
					</Typography>
				)}
			</Box>
		</Stack>
	);
};

// Hierarchical Envelope Node for a Spend Plan (sub-envelope)
const SpendPlanNode = ({ plan, currency }: { plan: EarmarkSpendPlan; currency: string }) => {
	const theme = useTheme();
	const [expanded, setExpanded] = useState(plan.status === 'APPROVED');

	const utilizationPct =
		plan.totalPlanned > 0
			? Math.round((plan.activities.reduce((s, a) => s + a.amount, 0) / plan.totalPlanned) * 100)
			: 0;

	return (
		<Box sx={{ ml: 3 }}>
			{/* Spend Plan Header */}
			<Stack
				direction="row"
				alignItems="center"
				spacing={2}
				onClick={() => setExpanded((p) => !p)}
				sx={{
					py: 1.5,
					px: 2,
					cursor: 'pointer',
					borderRadius: radius.sm,
					borderLeft: `3px solid ${STATUS_COLORS[plan.status] || '#6b7280'}`,
					'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) },
					transition: 'background-color 0.15s',
				}}>
				<IconButton size="small" sx={{ p: 0.5 }}>
					{expanded ? <IconCollapse /> : <IconExpand />}
				</IconButton>
				<Avatar
					sx={{
						width: 36,
						height: 36,
						bgcolor: alpha(STATUS_COLORS[plan.status] || '#6b7280', 0.1),
					}}>
					<IconSpendPlan sx={{ fontSize: 18, color: STATUS_COLORS[plan.status] || '#6b7280' }} />
				</Avatar>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography sx={{ fontSize: '1.15rem', fontWeight: 600 }}>{plan.name}</Typography>
						<Chip
							label={plan.status}
							size="small"
							sx={{
								bgcolor: alpha(STATUS_COLORS[plan.status] || '#6b7280', 0.1),
								color: STATUS_COLORS[plan.status] || '#6b7280',
								fontWeight: 600,
								fontSize: '0.8rem',
								height: 22,
							}}
						/>
					</Stack>
					<Typography sx={{ fontSize: '0.9rem', color: '#6b7280' }}>
						{plan.dateStart} &mdash; {plan.dateEnd} &middot; {plan.activities.length} activities
					</Typography>
				</Box>
				<Stack alignItems="flex-end" spacing={0.5}>
					<Typography sx={{ fontSize: '1.2rem', fontWeight: 700, fontFeatureSettings: '"tnum"' }}>
						{formatCurrency({ value: plan.totalPlanned, currency })}
					</Typography>
					{plan.totalPlannedLocal && (
						<Typography
							sx={{ fontSize: '0.85rem', color: '#6b7280', fontFeatureSettings: '"tnum"' }}>
							{plan.totalPlannedLocal}
						</Typography>
					)}
					<Stack direction="row" alignItems="center" spacing={1}>
						<LinearProgress
							variant="determinate"
							value={utilizationPct}
							sx={{
								width: 80,
								height: 6,
								borderRadius: 3,
								bgcolor: alpha(STATUS_COLORS[plan.status] || '#6b7280', 0.12),
								'& .MuiLinearProgress-bar': {
									bgcolor: STATUS_COLORS[plan.status] || '#6b7280',
									borderRadius: 3,
								},
							}}
						/>
						<Typography sx={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 }}>
							{utilizationPct}%
						</Typography>
					</Stack>
				</Stack>
			</Stack>

			{/* Activities */}
			<Collapse in={expanded}>
				{plan.activities.map((activity) => (
					<ActivityNode key={activity.id} activity={activity} currency={currency} />
				))}
			</Collapse>
		</Box>
	);
};

// Hierarchical Envelope Node for an Account (top-level envelope)
const AccountEnvelopeNode = ({ account }: { account: EarmarkAccount }) => {
	const theme = useTheme();
	const [expanded, setExpanded] = useState(true);

	const earmarkPct =
		account.balance > 0 ? Math.round((account.totalEarmarked / account.balance) * 100) : 0;

	return (
		<Paper
			elevation={0}
			sx={{
				border: `1px solid ${theme.palette.divider}`,
				borderRadius: radius.lg,
				overflow: 'hidden',
				mb: 2,
			}}>
			{/* Account Header */}
			<Stack
				direction="row"
				alignItems="center"
				spacing={2}
				onClick={() => setExpanded((p) => !p)}
				sx={{
					py: 2,
					px: 3,
					cursor: 'pointer',
					bgcolor: alpha(theme.palette.primary.main, 0.03),
					borderBottom: expanded ? `1px solid ${theme.palette.divider}` : 'none',
					'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
					transition: 'background-color 0.15s',
				}}>
				<IconButton size="small">{expanded ? <IconCollapse /> : <IconExpand />}</IconButton>
				<Avatar
					sx={{
						width: 44,
						height: 44,
						bgcolor: alpha(COLORS.primary.main, 0.1),
					}}>
					<IconAccount sx={{ fontSize: 24, color: COLORS.primary.main }} />
				</Avatar>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Stack direction="row" spacing={1} alignItems="center">
						<Typography sx={{ fontSize: '1.3rem', fontWeight: 700 }}>{account.name}</Typography>
						<Chip
							icon={<IconEnvelope sx={{ fontSize: 14 }} />}
							label={`${account.spendPlans.length} Spend Plans`}
							size="small"
							sx={{
								bgcolor: alpha(COLORS.primary.main, 0.08),
								color: COLORS.primary.main,
								fontWeight: 600,
								fontSize: '0.8rem',
								'& .MuiChip-icon': { color: COLORS.primary.main },
							}}
						/>
					</Stack>
					<Typography sx={{ fontSize: '0.95rem', color: '#6b7280' }}>
						{account.institution} &middot; {account.currency}
					</Typography>
				</Box>

				{/* Account financial summary */}
				<Stack direction="row" spacing={4} alignItems="center">
					<Stack alignItems="flex-end">
						<Typography
							sx={{
								fontSize: '0.8rem',
								color: '#9ca3af',
								fontWeight: 600,
								textTransform: 'uppercase',
								letterSpacing: '0.05em',
							}}>
							Balance
						</Typography>
						<Typography
							sx={{ fontSize: '1.35rem', fontWeight: 700, fontFeatureSettings: '"tnum"' }}>
							{formatCurrency({ value: account.balance, currency: account.currency })}
						</Typography>
					</Stack>
					<Stack alignItems="flex-end">
						<Typography
							sx={{
								fontSize: '0.8rem',
								color: '#9ca3af',
								fontWeight: 600,
								textTransform: 'uppercase',
								letterSpacing: '0.05em',
							}}>
							Earmarked
						</Typography>
						<Typography
							sx={{
								fontSize: '1.35rem',
								fontWeight: 700,
								color: COLORS.success.main,
								fontFeatureSettings: '"tnum"',
							}}>
							{formatCurrency({ value: account.totalEarmarked, currency: account.currency })}
						</Typography>
					</Stack>
					<Stack alignItems="flex-end">
						<Typography
							sx={{
								fontSize: '0.8rem',
								color: '#9ca3af',
								fontWeight: 600,
								textTransform: 'uppercase',
								letterSpacing: '0.05em',
							}}>
							Available
						</Typography>
						<Typography
							sx={{
								fontSize: '1.35rem',
								fontWeight: 700,
								color: account.unearmarked < 0 ? COLORS.error.main : COLORS.info.main,
								fontFeatureSettings: '"tnum"',
							}}>
							{formatCurrency({ value: account.unearmarked, currency: account.currency })}
						</Typography>
					</Stack>
					<Box sx={{ width: 80 }}>
						<Stack direction="row" justifyContent="flex-end" sx={{ mb: 0.5 }}>
							<Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: COLORS.primary.main }}>
								{earmarkPct}%
							</Typography>
						</Stack>
						<LinearProgress
							variant="determinate"
							value={Math.min(earmarkPct, 100)}
							sx={{
								height: 8,
								borderRadius: 4,
								bgcolor: alpha(COLORS.primary.main, 0.1),
								'& .MuiLinearProgress-bar': {
									bgcolor: earmarkPct > 100 ? COLORS.error.main : COLORS.primary.main,
									borderRadius: 4,
								},
							}}
						/>
					</Box>
				</Stack>
			</Stack>

			{/* Spend Plans (Sub-envelopes) */}
			<Collapse in={expanded}>
				<Box sx={{ py: 1 }}>
					{account.spendPlans.map((plan) => (
						<SpendPlanNode key={plan.id} plan={plan} currency={account.currency} />
					))}
				</Box>
			</Collapse>
		</Paper>
	);
};

// Custom Treemap Content

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- recharts Treemap injects layout props at runtime
const TreemapContent = (props: any) => {
	const { x, y, width, height, name, value, depth } = props;
	if (width < 30 || height < 20) return null;

	const bgColors = ['#eff6ff', '#dbeafe', '#bfdbfe'];
	const textColor = '#1e3a5f';

	return (
		<g>
			<rect
				x={x}
				y={y}
				width={width}
				height={height}
				rx={4}
				ry={4}
				style={{
					fill: bgColors[depth % 3] || '#eff6ff',
					stroke: '#93c5fd',
					strokeWidth: depth === 1 ? 2 : 1,
					strokeOpacity: 0.6,
				}}
			/>
			{width > 60 && height > 35 && (
				<>
					<text
						x={x + 8}
						y={y + 18}
						fill={textColor}
						fontSize={depth === 1 ? 13 : 11}
						fontWeight={depth === 1 ? 700 : 500}>
						{name?.length > Math.floor(width / 8)
							? name.substring(0, Math.floor(width / 8)) + '...'
							: name}
					</text>
					{height > 50 && (
						<text x={x + 8} y={y + 34} fill="#6b7280" fontSize={11}>
							${(value / 1_000_000).toFixed(1)}M
						</text>
					)}
				</>
			)}
		</g>
	);
};

// Main Component

export const FundingEarmarksReport = () => {
	const navigate = useNavigate();
	const theme = useTheme();
	const { t } = useTranslation();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { accounts, activities, workPlans, loading: ctxLoading } = useContext(ContextSpendPlan);

	// Project reporting currency drives every cross-account roll-up label and
	// the per-tab chart formatters on this report.
	const reportingCurrency = getProjectReportingCurrency(project) || 'USD';

	// state
	const [tabValue, setTabValue] = useState(0);
	const [liveSpendPlans, setLiveSpendPlans] = useState<SpendPlan[]>([]);
	const [liveLoading, setLiveLoading] = useState(false);
	const [expandAllAccounts, setExpandAllAccounts] = useState(true);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// =========================================================================
	// Load live data
	// =========================================================================
	useEffect(() => {
		if (!project?.id || !accessToken || !idToken) return;

		const load = async () => {
			setLiveLoading(true);
			try {
				const result = await fetchSpendPlans(
					{ accessToken, idToken },
					{ project: project.id, limit: 200, populate: 'account' }
				);
				if (result.error) {
					setBanner({
						message: result.error.message || 'Failed to load spend plans',
						severity: 'error',
					});
					return;
				}
				setLiveSpendPlans(result.data || []);
			} catch {
				setBanner({ message: 'Failed to load earmark data', severity: 'error' });
			} finally {
				setLiveLoading(false);
			}
		};

		load();
	}, [project?.id, accessToken, idToken, setBanner]);

	// =========================================================================
	// Transform live data into earmark structure
	// =========================================================================
	const earmarkData: EarmarkAccount[] = useMemo(() => {
		if (!accounts.length) return [];

		return accounts.map((account) => {
			const linkedPlans = liveSpendPlans.filter((sp) => {
				const spAccountId =
					typeof sp.account === 'object' && sp.account ? sp.account.id : sp.account;
				return spAccountId === account.id;
			});

			const totalEarmarked = linkedPlans.reduce((sum, sp) => sum + (sp.totalPlanned || 0), 0);
			const primaryBalance = account.balances?.[0];
			const balance = primaryBalance ? primaryBalance.balance : 0;
			const currency = primaryBalance?.currency || account.currency || 'USD';

			// Map live activities to each spend plan
			const spendPlanNodes: EarmarkSpendPlan[] = linkedPlans.map((sp) => {
				const spActivities: EarmarkActivity[] = activities
					.filter((a) => {
						const aWorkPlan =
							typeof a.workPlan === 'object' && a.workPlan ? a.workPlan.id : a.workPlan;
						const spWorkPlan =
							typeof sp.workPlan === 'object' && sp.workPlan ? sp.workPlan.id : sp.workPlan;
						return aWorkPlan === spWorkPlan;
					})
					.map((a) => ({
						id: a.id,
						name: typeof a.entity === 'string' ? a.entity : `Activity #${a.id}`,
						amount: a.amount || 0,
						status: String(a.status || 'PLANNED'),
						component: typeof a.component === 'string' ? a.component : undefined,
						category: typeof a.category === 'string' ? a.category : undefined,
						// Activities carry their captured local amount directly — no
						// restatement needed.
						amountLocal:
							a.amountLocal != null && a.currencyLocal
								? formatCurrency({ currency: a.currencyLocal, value: a.amountLocal })
								: null,
					}));

				// Restate the spend plan's planned total into the project's local
				// currency via the canonical resolver (inherits from the linked work plan).
				const spWorkPlanId =
					typeof sp.workPlan === 'object' && sp.workPlan ? sp.workPlan.id : sp.workPlan;
				const spWorkPlan = workPlans.find((wp) => wp.id === spWorkPlanId);
				const planFx = resolveSpendPlanFxRate(sp, spWorkPlan, project);
				const planCurrency = sp.currency || currency;
				const totalPlannedLocal =
					planFx && planCurrency !== planFx.localCurrency
						? formatCurrency({
								currency: planFx.localCurrency,
								value: (sp.totalPlanned || 0) * planFx.rate,
							})
						: null;

				return {
					id: sp.id,
					name: sp.name || `Spend Plan #${sp.id}`,
					totalPlanned: sp.totalPlanned || 0,
					status: sp.status || 'APPROVED',
					currency: sp.currency || currency,
					dateStart: sp.dateStart || '',
					dateEnd: sp.dateEnd || '',
					activities: spActivities,
					totalPlannedLocal,
				};
			});

			return {
				id: account.id,
				name: account.name || `Account #${account.id}`,
				institution: account.institution || '',
				currency,
				balance,
				spendPlans: spendPlanNodes,
				totalEarmarked,
				unearmarked: balance - totalEarmarked,
			};
		});
	}, [accounts, liveSpendPlans, activities, workPlans, project]);

	const loading = liveLoading || ctxLoading.accounts;

	// =========================================================================
	// Summary statistics — aggregated per currency so we never silently add
	// dollars and pesos. Reporting-currency totals are the headline; any other
	// currency totals are surfaced as a second line under each card. Spend-plan
	// and activity counts are currency-agnostic.
	// =========================================================================
	const summary = useMemo(() => {
		const byCurrency = new Map<string, { balance: number; earmarked: number }>();
		earmarkData.forEach((a) => {
			const entry = byCurrency.get(a.currency) || { balance: 0, earmarked: 0 };
			entry.balance += a.balance;
			entry.earmarked += a.totalEarmarked;
			byCurrency.set(a.currency, entry);
		});

		const reporting = byCurrency.get(reportingCurrency) || { balance: 0, earmarked: 0 };
		const others = Array.from(byCurrency.entries())
			.filter(([c]) => c !== reportingCurrency)
			.sort((a, b) => a[0].localeCompare(b[0]));

		const fmtOthers = (key: 'balance' | 'earmarked') =>
			others
				.filter(([, v]) => v[key] !== 0)
				.map(([c, v]) => `+ ${formatCurrency({ value: v[key], currency: c })}`)
				.join(' · ') || undefined;

		const otherBalanceLine = fmtOthers('balance');
		const otherEarmarkedLine = fmtOthers('earmarked');
		const otherUnearmarkedLine =
			others
				.map(([c, v]) => ({ c, v: v.balance - v.earmarked }))
				.filter((x) => x.v !== 0)
				.map((x) => `+ ${formatCurrency({ value: x.v, currency: x.c })}`)
				.join(' · ') || undefined;

		const totalSpendPlans = earmarkData.reduce((s, a) => s + a.spendPlans.length, 0);
		const totalActivities = earmarkData.reduce(
			(s, a) => s + a.spendPlans.reduce((ss, sp) => ss + sp.activities.length, 0),
			0
		);
		const activeSpendPlans = earmarkData.reduce(
			(s, a) => s + a.spendPlans.filter((sp) => sp.status === 'APPROVED').length,
			0
		);

		return {
			reportingBalance: reporting.balance,
			reportingEarmarked: reporting.earmarked,
			reportingUnearmarked: reporting.balance - reporting.earmarked,
			otherBalanceLine,
			otherEarmarkedLine,
			otherUnearmarkedLine,
			totalSpendPlans,
			totalActivities,
			activeSpendPlans,
		};
	}, [earmarkData, reportingCurrency]);

	// =========================================================================
	// Chart data transforms
	// =========================================================================

	// Pie chart: earmarked vs unearmarked per account
	const allocationPieData = useMemo(
		() =>
			earmarkData.map((a) => ({
				name: a.name,
				earmarked: a.totalEarmarked,
				unearmarked: Math.max(a.unearmarked, 0),
			})),
		[earmarkData]
	);

	// Bar chart: account comparison
	const accountBarData = useMemo(
		() =>
			earmarkData.map((a) => ({
				name: a.name.length > 20 ? a.name.substring(0, 20) + '...' : a.name,
				Balance: a.balance,
				Earmarked: a.totalEarmarked,
				Available: Math.max(a.unearmarked, 0),
			})),
		[earmarkData]
	);

	// Spend plan status breakdown
	const statusData = useMemo(() => {
		const counts: Record<string, number> = {};
		earmarkData.forEach((a) =>
			a.spendPlans.forEach((sp) => {
				counts[sp.status] = (counts[sp.status] || 0) + 1;
			})
		);
		return Object.entries(counts).map(([status, count]) => ({ name: status, value: count }));
	}, [earmarkData]);

	// Category breakdown for activities
	const categoryData = useMemo(() => {
		const amounts: Record<string, number> = {};
		earmarkData.forEach((a) =>
			a.spendPlans.forEach((sp) =>
				sp.activities.forEach((act) => {
					const cat = act.category || 'Other';
					amounts[cat] = (amounts[cat] || 0) + act.amount;
				})
			)
		);
		return Object.entries(amounts)
			.map(([name, value]) => ({ name, value }))
			.sort((a, b) => b.value - a.value);
	}, [earmarkData]);

	// Flatten treemap for recharts
	const flatTreemapData = useMemo(() => {
		const items: { name: string; size: number; account: string }[] = [];
		earmarkData.forEach((a) =>
			a.spendPlans.forEach((sp) =>
				items.push({ name: sp.name, size: sp.totalPlanned, account: a.name })
			)
		);
		return items;
	}, [earmarkData]);

	// =========================================================================
	// Render
	// =========================================================================

	return (
		<Box>
			{/* Header */}
			<HeaderListingPage
				icon={IconEarmark}
				title={t('spendPlan.fundingEarmarks')}
				subtitle={t('spendPlan.fundingEarmarks.report.subtitle')}
				variant="financial"
			/>

			{/* Content */}
			<Box sx={{ pb: 3, px: 3, pt: 2 }}>
				{/* Back link */}
				<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
					<Chip
						icon={<IconBack sx={{ fontSize: 16 }} />}
						label="Back to Reports"
						onClick={() => navigate(`/project/${project?.idWbg}/spend-plans/reports`)}
						sx={{
							fontWeight: 600,
							bgcolor: alpha(theme.palette.primary.main, 0.08),
							color: theme.palette.primary.main,
							'& .MuiChip-icon': { color: theme.palette.primary.main },
							'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15) },
							cursor: 'pointer',
						}}
					/>
				</Stack>

				{/* Loading indicator */}
				{loading && <LinearProgress sx={{ mb: 2, borderRadius: 2 }} />}

				{/* Summary Cards */}
				<StatCardRow
					loading={loading}
					cards={[
						{
							icon: IconAccount,
							label: 'Total Balance',
							value: formatCurrency({
								value: summary.reportingBalance,
								currency: reportingCurrency,
								notation: 'compact',
							}),
							valueLocal: summary.otherBalanceLine,
							color: COLORS.primary.main,
						},
						{
							icon: IconEarmark,
							label: 'Earmarked',
							value: formatCurrency({
								value: summary.reportingEarmarked,
								currency: reportingCurrency,
								notation: 'compact',
							}),
							valueLocal:
								summary.otherEarmarkedLine ||
								`${summary.totalSpendPlans} spend plan${summary.totalSpendPlans !== 1 ? 's' : ''}`,
							color: COLORS.success.main,
						},
						{
							icon: IconMoney,
							label: 'Unearmarked',
							value: formatCurrency({
								value: summary.reportingUnearmarked,
								currency: reportingCurrency,
								notation: 'compact',
							}),
							valueLocal: summary.otherUnearmarkedLine,
							color: summary.reportingUnearmarked < 0 ? COLORS.error.main : COLORS.info.main,
						},
						{
							icon: IconActivity,
							label: 'Activities',
							value: summary.totalActivities,
							valueLocal: `${summary.activeSpendPlans} approved`,
							color: COLORS.warning.main,
						},
					]}
				/>

				{/* Tabs */}
				<Paper
					elevation={0}
					sx={{
						border: `1px solid ${theme.palette.divider}`,
						borderRadius: radius.lg,
						mt: 3,
					}}>
					<Tabs
						value={tabValue}
						onChange={(_, v) => setTabValue(v)}
						sx={{
							px: 3,
							borderBottom: `1px solid ${theme.palette.divider}`,
							'& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '1rem' },
						}}>
						<Tab
							icon={<IconEnvelope sx={{ fontSize: 20 }} />}
							iconPosition="start"
							label="Envelope View"
						/>
						<Tab
							icon={<IconPieChart sx={{ fontSize: 20 }} />}
							iconPosition="start"
							label="Allocation Charts"
						/>
						<Tab
							icon={<IconTimeline sx={{ fontSize: 20 }} />}
							iconPosition="start"
							label="Breakdown Analysis"
						/>
					</Tabs>

					{/* Tab 0: Hierarchical Envelope View */}
					<TabPanel value={tabValue} index={0}>
						<Box sx={{ px: 3, pb: 3 }}>
							{/* Expand All toggle */}
							<Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
								<Chip
									icon={
										expandAllAccounts ? (
											<IconUnfoldLess sx={{ fontSize: 16 }} />
										) : (
											<IconUnfoldMore sx={{ fontSize: 16 }} />
										)
									}
									label={expandAllAccounts ? 'Collapse All' : 'Expand All'}
									onClick={() => setExpandAllAccounts((p) => !p)}
									sx={{
										fontWeight: 600,
										bgcolor: alpha(theme.palette.primary.main, 0.08),
										color: theme.palette.primary.main,
										'& .MuiChip-icon': { color: theme.palette.primary.main },
										cursor: 'pointer',
									}}
								/>
							</Stack>

							{earmarkData.length === 0 && !loading && (
								<Box sx={{ textAlign: 'center', py: 6 }}>
									<IconEarmark sx={{ fontSize: 64, color: '#d1d5db', mb: 2 }} />
									<Typography sx={{ fontSize: '1.15rem', color: '#6b7280' }}>
										No earmark data found. Create spend plans to see funding allocations.
									</Typography>
								</Box>
							)}

							{earmarkData.map((account) => (
								<AccountEnvelopeNode key={account.id} account={account} />
							))}

							{/* Legend */}
							{earmarkData.length > 0 && (
								<Paper
									elevation={0}
									sx={{
										mt: 3,
										p: 2,
										bgcolor: alpha(theme.palette.info.main, 0.04),
										border: `1px solid ${alpha(theme.palette.info.main, 0.12)}`,
										borderRadius: radius.sm,
									}}>
									<Stack direction="row" spacing={4} alignItems="center" flexWrap="wrap">
										<Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#374151' }}>
											Status Legend:
										</Typography>
										{Object.entries(STATUS_COLORS).map(([status, color]) => (
											<Stack key={status} direction="row" spacing={0.5} alignItems="center">
												<Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: color }} />
												<Typography sx={{ fontSize: '0.85rem', color: '#6b7280' }}>
													{status.replace('_', ' ')}
												</Typography>
											</Stack>
										))}
									</Stack>
								</Paper>
							)}
						</Box>
					</TabPanel>

					{/* Tab 1: Allocation Charts */}
					<TabPanel value={tabValue} index={1}>
						<Box sx={{ px: 3, pb: 3 }}>
							<Grid container spacing={3}>
								{/* Earmarked vs Available - Per Account Bar Chart */}
								<Grid item xs={12} lg={7}>
									<Paper
										elevation={0}
										sx={{
											p: 3,
											border: `1px solid ${theme.palette.divider}`,
											borderRadius: radius.sm,
											height: '100%',
										}}>
										<Typography sx={{ fontSize: '1.15rem', fontWeight: 700, mb: 2 }}>
											Account-Level Earmarking
										</Typography>
										<ResponsiveContainer width="100%" height={340}>
											<BarChart
												data={accountBarData}
												layout="vertical"
												margin={{ left: 20, right: 20 }}>
												<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
												<XAxis
													type="number"
													tickFormatter={(v) => `$${(v / 1_000_000).toFixed(0)}M`}
													tick={{ fontSize: 12 }}
												/>
												<YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 11 }} />
												<RechartsTooltip
													formatter={(value: number) =>
														formatCurrency({ value, currency: reportingCurrency })
													}
													contentStyle={{ borderRadius: 8, fontSize: 13 }}
												/>
												<Legend wrapperStyle={{ fontSize: 13 }} />
												<Bar
													dataKey="Earmarked"
													fill={COLORS.success.main}
													stackId="stack"
													radius={[0, 0, 0, 0]}
												/>
												<Bar
													dataKey="Available"
													fill={alpha(COLORS.info.main, 0.4)}
													stackId="stack"
													radius={[0, 4, 4, 0]}
												/>
											</BarChart>
										</ResponsiveContainer>
									</Paper>
								</Grid>

								{/* Spend Plan Status Pie */}
								<Grid item xs={12} lg={5}>
									<Paper
										elevation={0}
										sx={{
											p: 3,
											border: `1px solid ${theme.palette.divider}`,
											borderRadius: radius.sm,
											height: '100%',
										}}>
										<Typography sx={{ fontSize: '1.15rem', fontWeight: 700, mb: 2 }}>
											Spend Plan Status Distribution
										</Typography>
										<ResponsiveContainer width="100%" height={340}>
											<PieChart>
												<Pie
													data={statusData}
													cx="50%"
													cy="50%"
													innerRadius={70}
													outerRadius={120}
													paddingAngle={3}
													dataKey="value"
													label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
													labelLine={{ strokeWidth: 1 }}>
													{statusData.map((entry, index) => (
														<Cell
															key={entry.name}
															fill={
																STATUS_COLORS[entry.name] ||
																CHART_COLORS[index % CHART_COLORS.length]
															}
														/>
													))}
												</Pie>
												<RechartsTooltip contentStyle={{ borderRadius: 8, fontSize: 13 }} />
											</PieChart>
										</ResponsiveContainer>
									</Paper>
								</Grid>

								{/* Overall Earmark Allocation Donut */}
								<Grid item xs={12} md={6}>
									<Paper
										elevation={0}
										sx={{
											p: 3,
											border: `1px solid ${theme.palette.divider}`,
											borderRadius: radius.sm,
										}}>
										<Typography sx={{ fontSize: '1.15rem', fontWeight: 700, mb: 2 }}>
											Overall Fund Utilization
										</Typography>
										<ResponsiveContainer width="100%" height={300}>
											<PieChart>
												<Pie
													data={[
														{ name: 'Earmarked', value: summary.totalEarmarked },
														{ name: 'Unearmarked', value: Math.max(summary.totalUnearmarked, 0) },
													]}
													cx="50%"
													cy="50%"
													innerRadius={80}
													outerRadius={120}
													paddingAngle={2}
													dataKey="value"
													label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}>
													<Cell fill={COLORS.success.main} />
													<Cell fill={alpha(COLORS.info.main, 0.3)} />
												</Pie>
												<RechartsTooltip
													formatter={(value: number) =>
														formatCurrency({ value, currency: reportingCurrency })
													}
													contentStyle={{ borderRadius: 8, fontSize: 13 }}
												/>
												<Legend wrapperStyle={{ fontSize: 13 }} />
											</PieChart>
										</ResponsiveContainer>
										{/* Center label */}
										<Box sx={{ textAlign: 'center', mt: -2, mb: 1 }}>
											<Typography
												sx={{ fontSize: '2rem', fontWeight: 800, color: COLORS.success.main }}>
												{summary.totalBalance > 0
													? `${Math.round((summary.totalEarmarked / summary.totalBalance) * 100)}%`
													: '0%'}
											</Typography>
											<Typography sx={{ fontSize: '0.9rem', color: '#6b7280' }}>
												Total Utilization
											</Typography>
										</Box>
									</Paper>
								</Grid>

								{/* Per-Account Donut Breakdown */}
								<Grid item xs={12} md={6}>
									<Paper
										elevation={0}
										sx={{
											p: 3,
											border: `1px solid ${theme.palette.divider}`,
											borderRadius: radius.sm,
										}}>
										<Typography sx={{ fontSize: '1.15rem', fontWeight: 700, mb: 2 }}>
											Earmark Distribution by Account
										</Typography>
										<ResponsiveContainer width="100%" height={300}>
											<PieChart>
												<Pie
													data={allocationPieData.map((a) => ({
														name: a.name,
														value: a.earmarked,
													}))}
													cx="50%"
													cy="50%"
													outerRadius={120}
													paddingAngle={2}
													dataKey="value"
													label={({ name, percent }) => {
														const shortName =
															name.length > 18 ? name.substring(0, 18) + '...' : name;
														return `${shortName}: ${(percent * 100).toFixed(0)}%`;
													}}
													labelLine={{ strokeWidth: 1 }}>
													{allocationPieData.map((_, index) => (
														<Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
													))}
												</Pie>
												<RechartsTooltip
													formatter={(value: number) =>
														formatCurrency({ value, currency: reportingCurrency })
													}
													contentStyle={{ borderRadius: 8, fontSize: 13 }}
												/>
											</PieChart>
										</ResponsiveContainer>
									</Paper>
								</Grid>
							</Grid>
						</Box>
					</TabPanel>

					{/* Tab 2: Breakdown Analysis */}
					<TabPanel value={tabValue} index={2}>
						<Box sx={{ px: 3, pb: 3 }}>
							<Grid container spacing={3}>
								{/* Category Breakdown Bar Chart */}
								<Grid item xs={12} lg={7}>
									<Paper
										elevation={0}
										sx={{
											p: 3,
											border: `1px solid ${theme.palette.divider}`,
											borderRadius: radius.sm,
										}}>
										<Typography sx={{ fontSize: '1.15rem', fontWeight: 700, mb: 2 }}>
											Earmarked Funds by Category
										</Typography>
										<ResponsiveContainer width="100%" height={350}>
											<BarChart
												data={categoryData}
												layout="vertical"
												margin={{ left: 10, right: 20 }}>
												<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
												<XAxis
													type="number"
													tickFormatter={(v) => `$${(v / 1_000_000).toFixed(1)}M`}
													tick={{ fontSize: 12 }}
												/>
												<YAxis type="category" dataKey="name" width={160} tick={{ fontSize: 12 }} />
												<RechartsTooltip
													formatter={(value: number) =>
														formatCurrency({ value, currency: reportingCurrency })
													}
													contentStyle={{ borderRadius: 8, fontSize: 13 }}
												/>
												<Bar dataKey="value" fill={COLORS.primary.main} radius={[0, 6, 6, 0]}>
													{categoryData.map((_, index) => (
														<Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
													))}
												</Bar>
											</BarChart>
										</ResponsiveContainer>
									</Paper>
								</Grid>

								{/* Activity Status Breakdown */}
								<Grid item xs={12} lg={5}>
									<Paper
										elevation={0}
										sx={{
											p: 3,
											border: `1px solid ${theme.palette.divider}`,
											borderRadius: radius.sm,
										}}>
										<Typography sx={{ fontSize: '1.15rem', fontWeight: 700, mb: 2 }}>
											Activity Status Breakdown
										</Typography>
										{(() => {
											const activityStatusData: Record<string, { count: number; amount: number }> =
												{};
											earmarkData.forEach((a) =>
												a.spendPlans.forEach((sp) =>
													sp.activities.forEach((act) => {
														const status = act.status || 'UNKNOWN';
														if (!activityStatusData[status])
															activityStatusData[status] = { count: 0, amount: 0 };
														activityStatusData[status].count += 1;
														activityStatusData[status].amount += act.amount;
													})
												)
											);
											const items = Object.entries(activityStatusData);

											return (
												<Stack spacing={2}>
													{items.map(([status, data]) => {
														const totalAmt = summary.totalEarmarked || 1;
														const pct = Math.round((data.amount / totalAmt) * 100);
														return (
															<Box key={status}>
																<Stack
																	direction="row"
																	justifyContent="space-between"
																	alignItems="center"
																	sx={{ mb: 0.5 }}>
																	<Stack direction="row" spacing={1} alignItems="center">
																		<Box
																			sx={{
																				width: 10,
																				height: 10,
																				borderRadius: '50%',
																				bgcolor: STATUS_COLORS[status] || '#6b7280',
																			}}
																		/>
																		<Typography sx={{ fontSize: '1rem', fontWeight: 600 }}>
																			{status.replace('_', ' ')}
																		</Typography>
																		<Typography sx={{ fontSize: '0.9rem', color: '#9ca3af' }}>
																			({data.count} activities)
																		</Typography>
																	</Stack>
																	<Typography
																		sx={{
																			fontSize: '1rem',
																			fontWeight: 700,
																			fontFeatureSettings: '"tnum"',
																		}}>
																		{formatCurrency({
																			value: data.amount,
																			currency: reportingCurrency,
																		})}
																	</Typography>
																</Stack>
																<LinearProgress
																	variant="determinate"
																	value={pct}
																	sx={{
																		height: 8,
																		borderRadius: 4,
																		bgcolor: alpha(STATUS_COLORS[status] || '#6b7280', 0.1),
																		'& .MuiLinearProgress-bar': {
																			bgcolor: STATUS_COLORS[status] || '#6b7280',
																			borderRadius: 4,
																		},
																	}}
																/>
															</Box>
														);
													})}
												</Stack>
											);
										})()}
									</Paper>
								</Grid>

								{/* Treemap Visualization */}
								<Grid item xs={12}>
									<Paper
										elevation={0}
										sx={{
											p: 3,
											border: `1px solid ${theme.palette.divider}`,
											borderRadius: radius.sm,
										}}>
										<Typography sx={{ fontSize: '1.15rem', fontWeight: 700, mb: 1 }}>
											Earmark Proportional Map
										</Typography>
										<Typography sx={{ fontSize: '0.9rem', color: '#6b7280', mb: 2 }}>
											Size represents the relative amount earmarked for each spend plan across all
											accounts.
										</Typography>
										{flatTreemapData.length > 0 ? (
											<ResponsiveContainer width="100%" height={350}>
												<Treemap
													data={flatTreemapData}
													dataKey="size"
													nameKey="name"
													stroke="#fff"
													content={<TreemapContent />}>
													<RechartsTooltip
														formatter={(value: number) =>
															formatCurrency({ value, currency: reportingCurrency })
														}
														contentStyle={{ borderRadius: 8, fontSize: 13 }}
													/>
												</Treemap>
											</ResponsiveContainer>
										) : (
											<Box sx={{ textAlign: 'center', py: 4 }}>
												<Typography sx={{ fontSize: '1rem', color: '#9ca3af' }}>
													No spend plan data to visualize.
												</Typography>
											</Box>
										)}
									</Paper>
								</Grid>

								{/* Per-Account Spend Plan Comparison */}
								<Grid item xs={12}>
									<Paper
										elevation={0}
										sx={{
											p: 3,
											border: `1px solid ${theme.palette.divider}`,
											borderRadius: radius.sm,
										}}>
										<Typography sx={{ fontSize: '1.15rem', fontWeight: 700, mb: 2 }}>
											Spend Plan Comparison Across Accounts
										</Typography>
										{(() => {
											const barData = earmarkData.flatMap((a) =>
												a.spendPlans.map((sp) => ({
													name: sp.name.length > 25 ? sp.name.substring(0, 25) + '...' : sp.name,
													amount: sp.totalPlanned,
													account: a.name,
													status: sp.status,
												}))
											);
											return (
												<ResponsiveContainer
													width="100%"
													height={Math.max(barData.length * 50, 200)}>
													<BarChart
														data={barData}
														layout="vertical"
														margin={{ left: 20, right: 20 }}>
														<CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
														<XAxis
															type="number"
															tickFormatter={(v) => `$${(v / 1_000_000).toFixed(1)}M`}
															tick={{ fontSize: 12 }}
														/>
														<YAxis
															type="category"
															dataKey="name"
															width={200}
															tick={{ fontSize: 11 }}
														/>
														<RechartsTooltip
															formatter={(value: number) =>
																formatCurrency({ value, currency: reportingCurrency })
															}
															content={({ active, payload }) => {
																if (!active || !payload?.length) return null;
																const d = payload[0].payload;
																return (
																	<Paper sx={{ p: 1.5, borderRadius: 2, fontSize: 13 }}>
																		<Typography sx={{ fontWeight: 700, fontSize: 13 }}>
																			{d.name}
																		</Typography>
																		<Typography sx={{ fontSize: 12, color: '#6b7280' }}>
																			{d.account}
																		</Typography>
																		<Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.5 }}>
																			{formatCurrency({
																				value: d.amount,
																				currency: reportingCurrency,
																			})}
																		</Typography>
																		<Chip
																			label={d.status}
																			size="small"
																			sx={{
																				mt: 0.5,
																				bgcolor: alpha(STATUS_COLORS[d.status] || '#6b7280', 0.1),
																				color: STATUS_COLORS[d.status] || '#6b7280',
																				fontWeight: 600,
																				fontSize: 11,
																			}}
																		/>
																	</Paper>
																);
															}}
														/>
														<Bar dataKey="amount" radius={[0, 6, 6, 0]}>
															{barData.map((entry, index) => (
																<Cell
																	key={index}
																	fill={
																		STATUS_COLORS[entry.status] ||
																		CHART_COLORS[index % CHART_COLORS.length]
																	}
																/>
															))}
														</Bar>
													</BarChart>
												</ResponsiveContainer>
											);
										})()}
									</Paper>
								</Grid>
							</Grid>
						</Box>
					</TabPanel>
				</Paper>
			</Box>
		</Box>
	);
};
